<template>
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117] flex flex-col">

    <!-- ── Page header ── -->
    <div class="sticky top-0 z-20 flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-5 h-14">
        <h1 class="text-[15px] font-bold text-gray-900 dark:text-white">Settings</h1>
        <a v-if="useSettingsStoreObj().current_user.url"
          :href="'/' + useSettingsStoreObj().current_user.url"
          target="_blank"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
          </svg>
          {{ I18N.website_content.account_settings.view_profile }}
        </a>
      </div>
    </div>

    <!-- ── Body row ── -->
    <div class="flex flex-1">

      <!-- ── Desktop sidebar ── -->
      <aside class="hidden md:flex md:flex-col w-60 flex-shrink-0 bg-white dark:bg-[#1C2128] border-r border-gray-100 dark:border-gray-700 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">

        <!-- User card skeleton -->
        <div v-if="!useSettingsStoreObj().init_data_loaded"
          class="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700 animate-pulse">
          <div class="flex items-center gap-3 p-2.5 -mx-1">
            <div class="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 space-y-2 min-w-0">
              <div class="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
        </div>

        <!-- User card loaded -->
        <div v-else class="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
          <router-link to="/settings/profile/general" data-turbo-prefetch="false"
            class="flex items-center gap-3 p-2.5 -mx-1 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <div class="flex-shrink-0">
              <img v-if="useSettingsStoreObj().current_user.profile_picture_url"
                :src="useSettingsStoreObj().current_user.profile_picture_url"
                class="w-9 h-9 rounded-full object-cover"
                alt="avatar"/>
              <div v-else
                class="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
                <span class="text-sm font-bold text-white leading-none">
                  {{ useSettingsStoreObj().current_user.avatar_name }}
                </span>
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                {{ useSettingsStoreObj().current_user.full_name }}
              </p>
              <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                {{ useSettingsStoreObj().current_user.email }}
              </p>
            </div>
          </router-link>
        </div>

        <!-- Nav -->
        <nav class="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">

          <!-- ── Public Profile (expandable) ── -->
          <div>
            <button @click="toggleSubMenu('profile')"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="isSettingsProfile
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
              <span class="flex items-center gap-2.5 min-w-0">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                </svg>
                <span class="truncate">{{ I18N.website_content.account_settings.public_profile }}</span>
              </span>
              <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200"
                :class="show_profile_sub_menu ? 'rotate-180' : ''"
                fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
              </svg>
            </button>

            <div v-show="show_profile_sub_menu" class="mt-0.5 ml-3.5 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-0.5">
              <router-link to="/settings/profile/general" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/general'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.general }}
              </router-link>
              <router-link to="/settings/profile/branding" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/branding'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.branding }}
              </router-link>
              <router-link to="/settings/profile/themes" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/themes'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.themes_and_styles }}
              </router-link>
              <router-link to="/settings/profile/social_links" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/social_links'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.social_links }}
              </router-link>
              <router-link to="/settings/profile/policy" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/policy'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.custom_policy }}
              </router-link>
              <router-link to="/settings/profile/verification" data-turbo-prefetch="false"
                class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/profile/verification'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                Get Verified
                <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 leading-none">NEW</span>
              </router-link>
            </div>
          </div>

          <!-- ── Account ── -->
          <router-link to="/settings/account" data-turbo-prefetch="false" @click="closeSubMenu"
            class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="$route.path === '/settings/account'
              ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
            </svg>
            <span>{{ I18N.website_content.account_settings.account }}</span>
          </router-link>

          <!-- ── Appearance ── -->
          <router-link to="/settings/appearance" data-turbo-prefetch="false" @click="closeSubMenu"
            class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="$route.path === '/settings/appearance'
              ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z"/>
            </svg>
            <span>{{ I18N.website_content.account_settings.appearance }}</span>
          </router-link>

          <!-- ── Security ── -->
          <router-link to="/settings/security" data-turbo-prefetch="false" @click="closeSubMenu"
            class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="$route.path === '/settings/security'
              ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
            </svg>
            <span>{{ I18N.website_content.account_settings.security }}</span>
          </router-link>

          <!-- ── Developers (expandable) ── -->
          <div class="pt-1">
            <button @click="toggleSubMenu('developers')"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="isSettingsDevelopers
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
              <span class="flex items-center gap-2.5 min-w-0">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
                </svg>
                <span class="truncate">{{ I18N.website_content.account_settings.developers_console.title }}</span>
              </span>
              <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200"
                :class="show_developers_sub_menu ? 'rotate-180' : ''"
                fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
              </svg>
            </button>

            <div v-show="show_developers_sub_menu" class="mt-0.5 ml-3.5 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-0.5">
              <router-link to="/settings/developers/api_key" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/developers/api_key'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.dev_console_tab.title }}
              </router-link>
              <router-link to="/settings/developers/embed_code" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/developers/embed_code'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.dev_console_tab.embed_code }}
              </router-link>
              <router-link to="/settings/developers/qr_code" data-turbo-prefetch="false"
                class="flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors"
                :class="$route.path === '/settings/developers/qr_code'
                  ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/10'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'">
                {{ I18N.website_content.account_settings.dev_console_tab.qr_code }}
              </router-link>
            </div>
          </div>

        </nav>
      </aside>

      <!-- ── Right side: mobile tabs + main ── -->
      <div class="flex-1 min-w-0 flex flex-col">

        <!-- Mobile tab bar (hidden on desktop) -->
        <div class="md:hidden sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
          <div class="flex gap-1.5 px-4 py-2.5 overflow-x-auto no-scrollbar">
            <router-link to="/settings/profile/general" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/general' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ I18N.website_content.account_settings.general }}
            </router-link>
            <router-link to="/settings/profile/branding" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/branding' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ I18N.website_content.account_settings.branding }}
            </router-link>
            <router-link to="/settings/profile/themes" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/themes' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              Themes
            </router-link>
            <router-link to="/settings/profile/social_links" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/social_links' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              Social
            </router-link>
            <router-link to="/settings/profile/policy" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/policy' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              Policy
            </router-link>
            <router-link to="/settings/profile/verification" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/profile/verification' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              Get Verified
            </router-link>
            <router-link to="/settings/account" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/account' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ I18N.website_content.account_settings.account }}
            </router-link>
            <router-link to="/settings/appearance" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/appearance' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ I18N.website_content.account_settings.appearance }}
            </router-link>
            <router-link to="/settings/security" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path === '/settings/security' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              {{ I18N.website_content.account_settings.security }}
            </router-link>
            <router-link to="/settings/developers/api_key" data-turbo-prefetch="false"
              class="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
              :class="$route.path.startsWith('/settings/developers') ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
              Developers
            </router-link>
          </div>
        </div>

        <!-- Main content -->
        <div class="flex-1">

          <component :is="currentComponent" />

        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import Constants from "controllers/vue_components/mixins/constants"
import Profile from 'controllers/vue_components/settings/v1/profile'
import ProfileSocialLinks from 'controllers/vue_components/settings/v1/profile_social_links'
import ProfileThemes from 'controllers/vue_components/settings/v1/profile_themes'
import ProfileBranding from 'controllers/vue_components/settings/v1/profile_branding'
import CustomPolicy from 'controllers/vue_components/settings/v1/profile_custom_policy'
import Account from 'controllers/vue_components/settings/v1/account'
import Appearance from 'controllers/vue_components/settings/v1/appearance'
import Security from 'controllers/vue_components/settings/v1/security'
import DevelopersApiKey from 'controllers/vue_components/settings/v1/developers_console'
import DevelopersEmbedCode from 'controllers/vue_components/settings/v1/developers_embed'
import DevelopersQrCode from 'controllers/vue_components/settings/v1/developers_qrcode'
import ProfileVerification from 'controllers/vue_components/settings/v1/profile_verification'
const SETTINGS_ROUTE_MAP = {
    '/settings/profile/general': Profile,
    '/settings/profile/themes': ProfileThemes,
    '/settings/profile/social_links': ProfileSocialLinks,
    '/settings/profile/policy': CustomPolicy,
    '/settings/profile/branding': ProfileBranding,
    '/settings/profile/verification': ProfileVerification,
    '/settings/account': Account,
    '/settings/appearance': Appearance,
    '/settings/security': Security,
    '/settings/developers/api_key': DevelopersApiKey,
    '/settings/developers/qr_code': DevelopersQrCode,
    '/settings/developers/embed_code': DevelopersEmbedCode,
}

export default {
    name: 'SettingsIndex',
    mixins: [Constants],
    data() {
        return {
            show_profile_sub_menu: window.location.href.includes('/settings/profile') || false,
            isSettingsProfile: window.location.href.includes('/settings/profile'),
            show_developers_sub_menu: window.location.href.includes('/settings/developers') || false,
            isSettingsDevelopers: window.location.href.includes('/settings/developers'),
        }
    },
    methods: {
        useSettingsStoreObj() {
            return useSettingsStore()
        },
        toggleSubMenu(name) {
            if (name === 'profile') {
                this.show_profile_sub_menu = !this.show_profile_sub_menu
                this.show_developers_sub_menu = false
            }
            if (name === 'developers') {
                this.show_developers_sub_menu = !this.show_developers_sub_menu
                this.show_profile_sub_menu = false
            }
        },
        closeSubMenu() {
            this.show_profile_sub_menu = false
            this.show_developers_sub_menu = false
        },
        updateActiveSection() {
            this.isSettingsProfile = this.$route.path.startsWith('/settings/profile')
            this.isSettingsDevelopers = this.$route.path.startsWith('/settings/developers')
            if (this.isSettingsProfile) this.show_profile_sub_menu = true
            if (this.isSettingsDevelopers) this.show_developers_sub_menu = true
        },
    },
    computed: {
        currentComponent() {
            return SETTINGS_ROUTE_MAP[this.$route.path] || null
        },
    },
    created() {
        this.useSettingsStoreObj().fetchIfStale()
        this.useSettingsStoreObj().fetchProfileBannerSettings()
        this.useSettingsStoreObj().fetchUserPolicies()
    },
    mounted() {
        this.updateActiveSection()
    },
    watch: {
        $route() {
            this.updateActiveSection()
        },
    },
}
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }
</style>
